/**
 * AuthModal.jsx
 *
 * Fully self-contained auth modal — sign in, sign up, and create password
 * all live inside this one component. No page navigation. No sessionStorage
 * redirect tricks. When auth succeeds, onSuccess(user) is called and the
 * parent (FindJobRequestPage) continues its own flow.
 *
 * Props:
 *   isOpen      — boolean
 *   onClose     — () => void
 *   onSuccess   — (firebaseUser) => void
 *   initialMode — "signin" | "signup"  (default "signin")
 */

import { useState, useEffect, useRef, useCallback } from "react"
import {
  X,
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  Loader2,
  CheckCircle2,
} from "lucide-react"
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
} from "firebase/auth"
import { doc, setDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../services/firebase"
import { useAuth } from "../contexts/AuthProvider"
import InvitationService from "../services/InvitationService"
import { InvitationUtils } from "../utils/invitationUtils"
import OrganizationService from "../services/OrganizationService"
import { sendEmail } from "../services/emailService"

// ─────────────────────────────────────────────────────────────────────────────
// Internal screens (not exported)
// "signin-email" → "signin-password"
// "signup-email" → "signup-password"
// ─────────────────────────────────────────────────────────────────────────────
const SCREENS = {
  SIGNIN_EMAIL: "signin-email",
  SIGNIN_PASSWORD: "signin-password",
  SIGNUP_EMAIL: "signup-email",
  SIGNUP_PASSWORD: "signup-password",
}

// ─────────────────────────────────────────────────────────────────────────────
// Micro atoms
// ─────────────────────────────────────────────────────────────────────────────
const FieldError = ({ msg }) =>
  msg ? (
    <p className="flex items-center gap-1.5 text-[12px] text-red-500 mt-1.5 leading-snug">
      <AlertCircle size={11} className="flex-shrink-0" />
      {msg}
    </p>
  ) : null

const Spinner = ({ size = 14 }) => (
  <Loader2 size={size} className="animate-spin flex-shrink-0" />
)

// Shared input style — matches the page's clean system
const inputCls =
  "w-full px-3.5 py-3 text-sm border border-gray-200 hover:border-gray-300 " +
  "focus:border-gray-900 focus:ring-2 focus:ring-gray-900/8 outline-none " +
  "transition-all rounded-lg bg-white text-gray-900 placeholder-gray-300 font-satoshi"

const primaryBtn =
  "w-full py-3 text-sm font-bold bg-black text-white rounded-lg " +
  "hover:bg-gray-800 active:scale-[0.99] disabled:opacity-40 " +
  "transition-all flex items-center justify-center gap-2 font-satoshi"

// ─────────────────────────────────────────────────────────────────────────────
// Password strength meter
// ─────────────────────────────────────────────────────────────────────────────
const strength = (pw) => {
  if (!pw) return 0
  let s = 0
  if (pw.length >= 6) s++
  if (pw.length >= 10) s++
  if (/[A-Z]/.test(pw)) s++
  if (/[0-9]/.test(pw)) s++
  if (/[^A-Za-z0-9]/.test(pw)) s++
  return Math.min(s, 4)
}
const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"]
const strengthColor = [
  "",
  "bg-red-400",
  "bg-amber-400",
  "bg-blue-400",
  "bg-emerald-500",
]

const PasswordStrength = ({ password }) => {
  const s = strength(password)
  if (!password) return null
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-[3px] flex-1 rounded-full transition-all duration-300 ${
              i <= s ? strengthColor[s] : "bg-gray-100"
            }`}
          />
        ))}
      </div>
      <p
        className={`text-[11px] mt-1 font-medium ${
          s <= 1
            ? "text-red-500"
            : s === 2
              ? "text-amber-500"
              : s === 3
                ? "text-blue-500"
                : "text-emerald-600"
        }`}
      >
        {strengthLabel[s]}
      </p>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Email pill (shown when advancing past email step)
// ─────────────────────────────────────────────────────────────────────────────
const EmailPill = ({ email, onEdit }) => (
  <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3.5 py-2.5 mb-2">
    <span className="text-[13px] text-gray-700 font-medium truncate">
      {email}
    </span>
    <button
      type="button"
      onClick={onEdit}
      className="flex items-center gap-1 text-[11px] font-semibold text-gray-400 hover:text-gray-900 transition-colors ml-3 flex-shrink-0"
    >
      <ArrowLeft size={10} /> Edit
    </button>
  </div>
)

// ─────────────────────────────────────────────────────────────────────────────
// Firebase error map (shared across flows)
// ─────────────────────────────────────────────────────────────────────────────
const FB_ERRORS = {
  "auth/invalid-email": "Invalid email address.",
  "auth/user-disabled": "This account has been disabled.",
  "auth/user-not-found": "No account found with this email.",
  "auth/wrong-password": "Incorrect password.",
  "auth/invalid-credential": "Incorrect email or password.",
  "auth/too-many-requests": "Too many attempts — please try again later.",
  "auth/email-already-in-use": "An account with this email already exists.",
  "auth/weak-password": "Password must be at least 6 characters.",
}
const fbErr = (err) =>
  FB_ERRORS[err?.code] || err?.message || "Something went wrong."

// ─────────────────────────────────────────────────────────────────────────────
// Welcome email helpers (from CreatePasswordPage)
// ─────────────────────────────────────────────────────────────────────────────
const sendWelcomeEmail = (userName, email) => {
  sendEmail({
    email,
    subject: "Welcome to Resolut",
    text: `Hi ${userName},\n\nWelcome to the Resolut Club — we're excited to have you on board!\n\nAt Resolut, we act as a career concierge for top talent like yourself.\n\nPlease complete your profile: https://resolut.co.in/profile\n\nBest,\nPatrick\nCo-founder, Resolut`,
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const AuthModal = ({ isOpen, onClose, onSuccess, initialMode = "signin" }) => {
  const { refreshAuth } = useAuth()

  // ── Screen state ──────────────────────────────────────────────────────────
  const [screen, setScreen] = useState(
    initialMode === "signup" ? SCREENS.SIGNUP_EMAIL : SCREENS.SIGNIN_EMAIL,
  )

  // ── Shared form state ─────────────────────────────────────────────────────
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [userName, setUserName] = useState("")
  const [showPw, setShowPw] = useState(false)
  const [showConfirmPw, setShowConfirmPw] = useState(false)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  // ── Refs for auto-focus ───────────────────────────────────────────────────
  const emailRef = useRef(null)
  const passwordRef = useRef(null)
  const nameRef = useRef(null)

  // ── Reset everything when modal opens / mode changes ─────────────────────
  useEffect(() => {
    if (!isOpen) return
    setScreen(
      initialMode === "signup" ? SCREENS.SIGNUP_EMAIL : SCREENS.SIGNIN_EMAIL,
    )
    setEmail("")
    setPassword("")
    setConfirmPassword("")
    setUserName("")
    setShowPw(false)
    setShowConfirmPw(false)
    setError("")
    setLoading(false)
  }, [isOpen, initialMode])

  // Auto-focus on screen change
  useEffect(() => {
    const t = setTimeout(() => {
      if (screen === SCREENS.SIGNIN_EMAIL || screen === SCREENS.SIGNUP_EMAIL) {
        emailRef.current?.focus()
      } else if (screen === SCREENS.SIGNUP_PASSWORD) {
        nameRef.current?.focus()
      } else {
        passwordRef.current?.focus()
      }
    }, 80)
    return () => clearTimeout(t)
  }, [screen])

  // Clear error on any field change
  const clearErr = useCallback(() => setError(""), [])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [isOpen, onClose])

  if (!isOpen) return null

  // ── Shared helpers ────────────────────────────────────────────────────────
  const isSignIn =
    screen === SCREENS.SIGNIN_EMAIL || screen === SCREENS.SIGNIN_PASSWORD

  const switchMode = () => {
    setError("")
    setPassword("")
    setConfirmPassword("")
    setUserName("")
    if (isSignIn) {
      setScreen(SCREENS.SIGNUP_EMAIL)
    } else {
      setScreen(SCREENS.SIGNIN_EMAIL)
    }
  }

  // ── SIGN-IN: email step ───────────────────────────────────────────────────
  const handleSignInEmailNext = (e) => {
    e.preventDefault()
    setError("")
    if (!email || !email.includes("@")) {
      setError("Enter a valid email address.")
      return
    }
    setScreen(SCREENS.SIGNIN_PASSWORD)
  }

  // ── SIGN-IN: password step ────────────────────────────────────────────────
  const handleSignIn = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      const auth = getAuth()
      const cred = await signInWithEmailAndPassword(auth, email, password)

      // Handle pending invitation (mirrors LoginPage)
      const stored = InvitationUtils.getStoredInvitation()
      if (stored?.id) {
        try {
          await InvitationService.acceptInvitation(stored.id, cred.user.uid)
          InvitationUtils.clearInvitation()
        } catch (err) {
          console.warn("Invitation acceptance failed:", err)
        }
      }

      if (refreshAuth) await refreshAuth()
      await new Promise((r) => setTimeout(r, 300)) // settle
      onSuccess(cred.user)
    } catch (err) {
      setError(fbErr(err))
    } finally {
      setLoading(false)
    }
  }

  // ── SIGN-UP: email step ───────────────────────────────────────────────────
  const handleSignUpEmailNext = async (e) => {
    e.preventDefault()
    setError("")
    if (!email || !email.includes("@")) {
      setError("Enter a valid email address.")
      return
    }
    setLoading(true)
    try {
      const auth = getAuth()
      const methods = await fetchSignInMethodsForEmail(auth, email)
      if (methods.length > 0) {
        setError("An account with this email already exists. Sign in instead.")
        return
      }
      setScreen(SCREENS.SIGNUP_PASSWORD)
    } catch (err) {
      setError(fbErr(err))
    } finally {
      setLoading(false)
    }
  }

  // ── SIGN-UP: create password + account ───────────────────────────────────
  const handleCreateAccount = async (e) => {
    e.preventDefault()
    setError("")

    if (!userName.trim()) {
      setError("Please enter your name.")
      return
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.")
      return
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.")
      return
    }

    setLoading(true)
    try {
      const auth = getAuth()
      const cred = await createUserWithEmailAndPassword(auth, email, password)
      const user = cred.user

      // Create Firestore user doc (mirrors CreatePasswordPage exactly)
      await setDoc(doc(db, "users", user.uid), {
        email,
        name: userName,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        accountType: "candidate",
      })

      // Handle stored invitation
      const storedInvitation = InvitationUtils.getStoredInvitation()
      if (storedInvitation?.id) {
        try {
          await InvitationService.acceptInvitation(
            storedInvitation.id,
            user.uid,
          )
          InvitationUtils.clearInvitation()
        } catch (err) {
          console.warn("Invitation acceptance failed:", err)
        }
      }

      if (refreshAuth) await refreshAuth()

      // Send welcome email (fire-and-forget)
      try {
        sendWelcomeEmail(userName, email)
      } catch (_) {}

      await new Promise((r) => setTimeout(r, 500)) // settle
      onSuccess(user)
    } catch (err) {
      setError(fbErr(err))
    } finally {
      setLoading(false)
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      {/* Panel */}
      <div
        className="relative bg-white w-full max-w-[400px] rounded-2xl shadow-2xl shadow-black/20 overflow-hidden font-satoshi"
        style={{
          animation: "modalIn 0.18s cubic-bezier(0.34,1.56,0.64,1) both",
        }}
      >
        <style>{`
          @keyframes modalIn {
            from { opacity: 0; transform: scale(0.95) translateY(8px); }
            to   { opacity: 1; transform: scale(1)    translateY(0); }
          }
        `}</style>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="px-7 pt-7 pb-5 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              {/* Mode label */}
              <p className="text-[9px] font-black tracking-[0.22em] uppercase text-gray-400 mb-1.5">
                {isSignIn ? "Welcome back" : "Create account"}
              </p>
              {/* Dynamic heading per screen */}
              <h2 className="text-xl font-black text-gray-900 tracking-tight leading-tight">
                {screen === SCREENS.SIGNIN_EMAIL && "Sign in to continue"}
                {screen === SCREENS.SIGNIN_PASSWORD && "Enter your password"}
                {screen === SCREENS.SIGNUP_EMAIL && "Start your search"}
                {screen === SCREENS.SIGNUP_PASSWORD && "Almost there"}
              </h2>
              <p className="text-[12px] text-gray-400 mt-1 leading-snug">
                {screen === SCREENS.SIGNIN_EMAIL &&
                  "Access your job matches and alerts."}
                {screen === SCREENS.SIGNIN_PASSWORD &&
                  "One more step to see your matches."}
                {screen === SCREENS.SIGNUP_EMAIL &&
                  "Create an account to unlock your job matches."}
                {screen === SCREENS.SIGNUP_PASSWORD &&
                  "Set a password and you're all set."}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-gray-700 hover:bg-gray-100 transition-all flex-shrink-0 -mt-0.5"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className="px-7 py-6">
          {/* Global error banner */}
          {error && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-100 rounded-xl p-3.5 mb-5">
              <AlertCircle
                size={13}
                className="text-red-500 flex-shrink-0 mt-0.5"
              />
              <p className="text-[12px] text-red-700 leading-snug">{error}</p>
            </div>
          )}

          {/* ── SIGN-IN: email ─────────────────────────────────────────── */}
          {screen === SCREENS.SIGNIN_EMAIL && (
            <form
              onSubmit={handleSignInEmailNext}
              className="flex flex-col gap-4"
              noValidate
            >
              <div>
                <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 block mb-1.5">
                  Email address
                </label>
                <input
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    clearErr()
                  }}
                  placeholder="you@example.com"
                  className={inputCls}
                  autoComplete="email"
                />
              </div>
              <button type="submit" className={primaryBtn}>
                Continue <ArrowRight size={13} />
              </button>
            </form>
          )}

          {/* ── SIGN-IN: password ──────────────────────────────────────── */}
          {screen === SCREENS.SIGNIN_PASSWORD && (
            <form
              onSubmit={handleSignIn}
              className="flex flex-col gap-4"
              noValidate
            >
              <EmailPill
                email={email}
                onEdit={() => {
                  setScreen(SCREENS.SIGNIN_EMAIL)
                  setError("")
                  setPassword("")
                }}
              />
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400">
                    Password
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-[11px] text-gray-400 hover:text-gray-900 transition-colors"
                    tabIndex={-1}
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    ref={passwordRef}
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      clearErr()
                    }}
                    placeholder="••••••••"
                    className={inputCls + " pr-10"}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className={primaryBtn}>
                {loading ? (
                  <>
                    <Spinner /> Signing in…
                  </>
                ) : (
                  <>
                    Sign in <ArrowRight size={13} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* ── SIGN-UP: email ─────────────────────────────────────────── */}
          {screen === SCREENS.SIGNUP_EMAIL && (
            <form
              onSubmit={handleSignUpEmailNext}
              className="flex flex-col gap-4"
              noValidate
            >
              <div>
                <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 block mb-1.5">
                  Email address
                </label>
                <input
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    clearErr()
                  }}
                  placeholder="you@example.com"
                  className={inputCls}
                  autoComplete="email"
                />
              </div>
              <button type="submit" disabled={loading} className={primaryBtn}>
                {loading ? (
                  <>
                    <Spinner /> Checking…
                  </>
                ) : (
                  <>
                    Continue <ArrowRight size={13} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* ── SIGN-UP: name + password ───────────────────────────────── */}
          {screen === SCREENS.SIGNUP_PASSWORD && (
            <form
              onSubmit={handleCreateAccount}
              className="flex flex-col gap-4"
              noValidate
            >
              <EmailPill
                email={email}
                onEdit={() => {
                  setScreen(SCREENS.SIGNUP_EMAIL)
                  setError("")
                  setPassword("")
                  setConfirmPassword("")
                  setUserName("")
                }}
              />

              {/* Name */}
              <div>
                <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 block mb-1.5">
                  Your name
                </label>
                <input
                  ref={nameRef}
                  type="text"
                  value={userName}
                  onChange={(e) => {
                    setUserName(e.target.value)
                    clearErr()
                  }}
                  placeholder="Aryan Mehta"
                  className={inputCls}
                  autoComplete="name"
                />
              </div>

              {/* Password */}
              <div>
                <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 block mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    ref={passwordRef}
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      clearErr()
                    }}
                    placeholder="At least 6 characters"
                    className={inputCls + " pr-10"}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <PasswordStrength password={password} />
              </div>

              {/* Confirm password */}
              <div>
                <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-gray-400 block mb-1.5">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPw ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value)
                      clearErr()
                    }}
                    placeholder="••••••••"
                    className={inputCls + " pr-10"}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirmPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  {/* Inline match check */}
                  {confirmPassword && (
                    <span className="absolute right-9 top-1/2 -translate-y-1/2">
                      {password === confirmPassword ? (
                        <CheckCircle2 size={13} className="text-emerald-500" />
                      ) : (
                        <X size={13} className="text-red-400" />
                      )}
                    </span>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={primaryBtn + " mt-1"}
              >
                {loading ? (
                  <>
                    <Spinner /> Creating account…
                  </>
                ) : (
                  <>
                    Create account <ArrowRight size={13} />
                  </>
                )}
              </button>

              <p className="text-[11px] text-gray-400 text-center leading-relaxed -mt-1">
                By creating an account you agree to our{" "}
                <a href="/terms" className="text-gray-600 hover:underline">
                  Terms
                </a>{" "}
                and{" "}
                <a href="/privacy" className="text-gray-600 hover:underline">
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          )}

          {/* ── Mode toggle ────────────────────────────────────────────── */}
          <div className="mt-6 pt-5 border-t border-gray-100 text-center">
            <p className="text-[12px] text-gray-400">
              {isSignIn
                ? "Don't have an account? "
                : "Already have an account? "}
              <button
                type="button"
                onClick={switchMode}
                className="text-[12px] font-bold text-gray-900 hover:underline"
              >
                {isSignIn ? "Sign up" : "Sign in"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthModal
